import * as React from "react";
import { useEffect, useMemo } from "react";
import { SmartApplyTemplate } from "../../../template/smart-apply/default-template";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { AppDetailBasicInfo } from "./basic-info";
import { AppDetailIssueFile } from "./issue-files";
import {
  ApplicationDetailContextProps,
  ApplicationDetailService,
} from "../../../../application/service/smart-apply/application-detail/application-detail-service";
import { TabUiState } from "../../../../application/service/smart-apply/application-detail/tab-ui-state";
import { connectPageService } from "@graffer-inc/smart-apply";
import { SmartApplyR } from "../route";
import { ApplyFormInfoModification } from "./form/form-info-modification";
import { useAuthContextV2 } from "../../../../auth-state";
import { EventTimeline } from "./timeline";
import { parseQueryOfLocation } from "../../../../utils/url-utils";
import { PaymentV2 } from "./payment-v2";
import {
  SecondaryMenuList,
  Spinner,
  WarningBanner,
} from "@graffer-inc/console-design-kit";
import { TenantProcessResultPage } from "./tenant-process-result";
import { ApplicationDetailHeader } from "./contents-header";

const ApplicationDetail: React.FC<ApplicationDetailContextProps> = ({
  loadingStates: {
    isPDFOutputting,
    isLoading,
    modifyingProcess,
    isBulkDownloading,
  },
  tabState,
  setTabUIState,
  isInitialized,
  application,
  onAddPayment,
  procedure,
  histories,
  events,
  messages,
  internalComments,
  onTabChange,
  onPageMount,
  onFileIssued,
  onIssueFileDeleted,
  onPDFOutputRequested,
  onPDFOutputForCustomerRequested,
  onStatusUpdated,
  onWithdrawPostPayment,
  paymentSummary,
  paymentDetailsByPayment,
  onModify,
  onModificationEnd,
  refundV2,
  adjustExceedingOrDeficiency,
  onBulkDownload,
  selectedMetaIds,
  toggleCsvMetaCheck,
  toggleAllCsvMetaCheck,
  downloadSingleRecordCSV,
  csvEncoding,
  onChangeCSVEncoding,
  includeTenantProcessResult,
  onCheckIncludeTenantProcessResult,
  requireSelectableRawValue,
  onChangeRequireSelectableRawValue,
  onUpdateTenantProcessResult,
  onRevertStatusToWip,
}) => {
  const authState = useAuthContextV2();
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const { tab } = parseQueryOfLocation(useLocation()) as {
    tab: string | undefined;
  };

  const handleOnGoToApplicationList = React.useCallback(() => {
    history.push(SmartApplyR.Applications.link({}));
  }, [history]);

  useEffect(() => {
    onTabChange(tab as string | undefined);
  }, [onTabChange, tab]);

  useEffect(() => {
    onPageMount(id);
  }, [onPageMount, id, tab]);

  const applicationRemovalTaskScheduledEvent = useMemo(() => {
    const applicationRemovalTaskEvents =
      events.convert((appEvents) =>
        appEvents.filter(
          (e) =>
            e.eventType === "RemovalTaskScheduled" ||
            e.eventType === "RemovalTaskCanceled",
        ),
      ).data || [];
    if (applicationRemovalTaskEvents.length === 0) {
      return undefined;
    }
    const latestEvent = applicationRemovalTaskEvents.reduce(
      (latest, current) => {
        return latest.createdAt > current.createdAt ? latest : current;
      },
    );
    return latestEvent.eventType === "RemovalTaskScheduled"
      ? latestEvent
      : undefined;
  }, [events]);

  if (!isInitialized()) {
    return null;
  }

  // hasCreatedEvent controls displaying timeline; old data do not have events,
  // thus timeline tab will be hidden for those applications.
  const hasCreatedEvent: boolean =
    events.convert((appEvents) =>
      appEvents.some((e) => e.eventType === "Created"),
    ).data || false;

  return (
    <SmartApplyTemplate
      breadcrumbs={[
        { to: SmartApplyR.Applications.link({}), title: "申請一覧" },
        {
          to: SmartApplyR.ApplicationDetail.link({ id: application?.id || "" }),
          title: application?.formattedId || "",
        },
      ]}
      contentsHeader={
        <ApplicationDetailHeader
          application={application}
          procedure={procedure}
          validRemovalTask={
            applicationRemovalTaskScheduledEvent
              ? {
                  unixtime:
                    applicationRemovalTaskScheduledEvent.data
                      .scheduledRemoveUnixtime,
                  id: applicationRemovalTaskScheduledEvent.data.taskId,
                }
              : undefined
          }
          updateApplyStatus={onStatusUpdated}
          onRevertStatusToWip={onRevertStatusToWip}
        >
          <>
            {procedure?.requireJPKISignature() &&
              application?.hasSignature() &&
              application.matchSignatureVerifyStatus({
                IS_INVALID: (
                  <WarningBanner
                    message={
                      "この申請に付された電子署名が無効であることが確認されました。\n住所や氏名の変更や利用者による届出により、失効されている可能性があります。"
                    }
                  />
                ),
                IS_PENDING: (
                  <WarningBanner
                    message={
                      "この申請に付された電子署名は現在、有効性の検証中です。申請の処理を行う場合は注意してください。"
                    }
                  />
                ),
                IS_VALID: null,
              })}
          </>
        </ApplicationDetailHeader>
      }
      menuList={
        <SecondaryMenuList
          items={[
            {
              title: "基本情報",
              anchor: ({ children, ...props }) => (
                <button
                  onClick={() => setTabUIState(TabUiState.initialize())}
                  {...props}
                >
                  {children}
                </button>
              ),
              active: tabState.equals("BASIC"),
              hidden: false,
            },
            {
              title: "申請内容",
              anchor: ({ children, ...props }) => (
                <button
                  onClick={() => setTabUIState(TabUiState.form())}
                  {...props}
                >
                  {children}
                </button>
              ),
              active: tabState.equals("FORM"),
              hidden: false,
            },
            {
              title: "支払い情報",
              anchor: ({ children, ...props }) => (
                <button
                  onClick={() => setTabUIState(TabUiState.payment())}
                  {...props}
                >
                  {children}
                </button>
              ),
              active: tabState.equals("PAYMENT"),
              hidden: !authState.auth.usePayment,
            },
            {
              title: "管理情報",
              anchor: ({ children, ...props }) => (
                <button
                  onClick={() =>
                    setTabUIState(TabUiState.tenantProcessResult())
                  }
                  {...props}
                >
                  {children}
                </button>
              ),
              active: tabState.equals("TENANT_PROCESS_RESULT"),
              hidden: false,
            },
            {
              title: "交付物",
              anchor: ({ children, ...props }) => (
                <button
                  onClick={() => setTabUIState(TabUiState.issueFile())}
                  {...props}
                >
                  {children}
                </button>
              ),
              active: tabState.equals("ISSUE_FILE"),
              hidden: false,
            },
            {
              title: "タイムライン",
              anchor: ({ children, ...props }) => (
                <button
                  onClick={() => setTabUIState(TabUiState.timeline())}
                  {...props}
                >
                  {children}
                </button>
              ),
              active: tabState.equals("TIMELINE"),
              hidden: !hasCreatedEvent,
            },
          ]}
          parent={{
            title: "申請一覧",
            anchor: ({ children, ...props }) => (
              <button onClick={handleOnGoToApplicationList} {...props}>
                {children}
              </button>
            ),
          }}
        />
      }
    >
      {tabState.matchType({
        BASIC: (
          <AppDetailBasicInfo
            appl={application!}
            procedure={procedure!}
            updateApplyStatus={onStatusUpdated}
            onRevertStatusToWip={onRevertStatusToWip}
            refreshApplication={onPageMount}
            removalIsScheduled={!!applicationRemovalTaskScheduledEvent}
          />
        ),
        FORM: (
          <ApplyFormInfoModification
            isPDFOutputting={isPDFOutputting}
            outputPDF={onPDFOutputRequested}
            outputPDFForCustomer={onPDFOutputForCustomerRequested}
            proc={procedure!}
            application={application!}
            histories={histories}
            onModify={onModify}
            onModificationEnd={onModificationEnd}
            modifyingProcess={modifyingProcess}
            onBulkDownload={onBulkDownload}
            isBulkDownloading={isBulkDownloading}
            selectMetaIds={selectedMetaIds}
            toggleCsvMetaCheck={toggleCsvMetaCheck}
            toggleAllCsvMetaCheck={toggleAllCsvMetaCheck}
            downloadSingleRecordCSV={downloadSingleRecordCSV}
            csvEncoding={csvEncoding}
            onChangeCSVEncoding={onChangeCSVEncoding}
            includeTenantProcessResult={includeTenantProcessResult}
            onCheckIncludeTenantProcessResult={
              onCheckIncludeTenantProcessResult
            }
            requireSelectableRawValue={requireSelectableRawValue}
            onChangeRequireSelectableRawValue={
              onChangeRequireSelectableRawValue
            }
          />
        ),
        PAYMENT: (
          <PaymentV2
            application={application!}
            procedure={procedure!}
            paymentSummary={paymentSummary}
            paymentDetailByPayments={paymentDetailsByPayment}
            addPayment={onAddPayment}
            withdrawPayment={onWithdrawPostPayment}
            refund={refundV2}
            adjustExceedingOrDeficiency={adjustExceedingOrDeficiency}
          />
        ),
        TENANT_PROCESS_RESULT: (
          <TenantProcessResultPage
            application={application!}
            procedure={procedure!}
            onUpdate={onUpdateTenantProcessResult}
          />
        ),
        ISSUE_FILE: (
          <AppDetailIssueFile
            application={application!}
            deleteFile={onIssueFileDeleted}
            addFile={onFileIssued}
            isLoading={isLoading}
          />
        ),
        TIMELINE: hasCreatedEvent && (
          <EventTimeline
            app={application!}
            events={events}
            messages={messages}
            internalComments={internalComments}
            procedure={procedure!}
            histories={histories}
          />
        ),
      })}
      <Spinner
        loading={isPDFOutputting || modifyingProcess.state === "loading"}
      />
    </SmartApplyTemplate>
  );
};

export const ApplicationDetailContainer = connectPageService(
  ApplicationDetail,
  () => new ApplicationDetailService(),
);
